import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { from, interval, of } from 'rxjs';
import { filter, map, switchMap, take } from 'rxjs/operators';

import { NotificationService } from '@alfresco/adf-core';

import {
  CancelCheckoutAction,
  CANCEL_CHECKOUT,
  CheckinNodeAction,
  CHECKIN_NODE,
  CheckoutNodesAction,
  CHECKOUT_NODES_ACTION,
  SnackbarInfoAction,
} from '../actions/app.custom-actions';
import { SaveVersionsComponent } from '../../dialogs/save-versions/save-versions.dialog';
import { CustomContentManagementService } from '../../services/custom-content-management.service';
import { CitationService } from '../../services/citations.service';
import { AppStore } from '../states/app.state';
import {
  getAppSelection,
  getCurrentSiteUserRole,
} from '../selectors/app.custom-selectors';
import { NodeInfoService } from '../../services/node.service';
import { WKMessageDialogService } from '../../components/shared/dialogs/message-dialog/message.dialog.service';
import { WkAlfrescoApiService } from '../../services/wk-alfresco-api.service';
import { WkAppConfigService } from '../../services';
import { CommonConstants } from '../../shared';
import { WkConfirmCancelCheckoutComponent } from '../../dialogs/wk-confirm-cancel-checkout/wk-confirm-cancel-checkout.component';

const jsonContentType = ['application/json'];

@Injectable()
export class NodeCheckoutEffects {
  private readonly notificationService: NotificationService;
  private readonly citationService: CitationService;
  private readonly router: Router;
  private readonly dialogRef: MatDialog;
  private readonly customContentService: CustomContentManagementService;
  private readonly apiService: WkAlfrescoApiService;
  private readonly nodeInfoService: NodeInfoService;
  private readonly messageService: WKMessageDialogService;
  private readonly appConfig: WkAppConfigService;

  private readonly siteConsumerRole = 'SiteConsumer';
  private readonly permissionError = `You don't have Permission to edit document`;

  constructor(
    injector: Injector,
    private readonly store: Store<AppStore>,
    private readonly actions$: Actions
  ) {
    this.notificationService =
      injector.get<NotificationService>(NotificationService);
    this.citationService = injector.get<CitationService>(CitationService);
    this.router = injector.get<Router>(Router);
    this.dialogRef = injector.get<MatDialog>(MatDialog);
    this.customContentService = injector.get<CustomContentManagementService>(
      CustomContentManagementService
    );
    this.apiService = injector.get<WkAlfrescoApiService>(WkAlfrescoApiService);
    this.nodeInfoService = injector.get<NodeInfoService>(NodeInfoService);
    this.messageService = injector.get<WKMessageDialogService>(
      WKMessageDialogService
    );
    this.appConfig = injector.get<WkAppConfigService>(WkAppConfigService);
  }

  checkoutNodesAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CheckoutNodesAction>(CHECKOUT_NODES_ACTION),
        map((action) => {
          if (action?.payload?.length > 0) {
            this.customContentService.checkoutNode();
          } else {
            this.store
              .select(getAppSelection)
              .pipe(take(1))
              .subscribe((selection) => {
                if (selection && !selection.isEmpty) {
                  const batchNodesIds = selection.nodes.map(
                    (node) => node.entry.id
                  );
                  const checkoutBatchParams = {
                    ids: batchNodesIds,
                  };

                  from(
                    this.apiService.callContentClientCustomApi({
                      path: `alfresco/service/wk/nodes/checkout`,
                      httpMethod: 'POST',
                      bodyParam: checkoutBatchParams,
                      contentTypes: jsonContentType,
                      accepts: jsonContentType,
                    })
                  ).subscribe({
                    next: (data: any) => {
                      if (!data.ids[0]) {
                        this.store.dispatch(
                          new SnackbarInfoAction(
                            'APP.MESSAGES.ERRORS.CONCURRENT_EDIT_IN_ARBORTEXT_ERROR'
                          )
                        );
                      }
                      this.customContentService.checkoutNode();
                    },
                    error: () => {
                      this.notificationService.openSnackMessage(
                        'Cannot Check Out',
                        3000
                      );
                    },
                  });
                }
              });
          }
        })
      ),
    { dispatch: false }
  );

  CheckinNodeAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CheckinNodeAction>(CHECKIN_NODE),
        map(() => {
          this.store
            .select(getCurrentSiteUserRole)
            .pipe(take(1))
            .subscribe((userRole) => {
              if (userRole === this.siteConsumerRole) {
                this.notificationService.showError(this.permissionError);
                return;
              }
              this.checkin();
            });
        })
      ),
    { dispatch: false }
  );

  checkin() {
    if (this.router.url.split('?')[0].includes('edit')) {
      this.citationService.triggerDocumentSaveAndCheckin.next(true);
    } else {
      this.store
        .select(getAppSelection)
        .pipe(take(1))
        .subscribe((selection) => {
          if (selection && !selection.isEmpty) {
            const node = selection.nodes[0].entry;
            this.checkEmptyCitation(node.id).subscribe({
              next: (isCitationValidData) => {
                if (isCitationValidData) {
                  const dialogRef = this.dialogRef.open(SaveVersionsComponent, {
                    data: node,
                    panelClass: 'save-version-dialog-panel',
                    width: '630px',
                  });

                  dialogRef.afterClosed().subscribe((data) => {
                    data && this.checkInNode(selection, data, node);
                  });
                } else {
                  this.messageService.emptyCitationPopup();
                }
              },
            });
          }
        });
    }
  }

  private checkInNode(selection, data, node) {
    const batchNodesIds = selection.nodes.map(
      (batchNode) => batchNode.entry.id
    );
    this.nodeInfoService
      .checkIn(batchNodesIds, data, 'preview')
      .then((res: any) => {
        this.updateCurrentUrl(res, node, true);
      })
      .catch(() =>
        this.notificationService.openSnackMessage('Cannot Check In', 3000)
      );
  }

  private checkEmptyCitation(nodeId: string) {
    return from(this.apiService.nodesApi.getNode(nodeId)).pipe(
      take(1),
      map((freshNode: any) => {
        const isCitationValid =
          freshNode?.entry?.properties?.[CommonConstants.isCitationValid];
        return isCitationValid !== false;
      })
    );
  }

  cancelCheckoutAction$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CancelCheckoutAction>(CANCEL_CHECKOUT),
        map((action) => {
          this.store
            .select(getCurrentSiteUserRole)
            .pipe(
              take(1),
              filter((userRole) => {
                if (userRole === this.siteConsumerRole) {
                  this.notificationService.showError(this.permissionError);
                  return false;
                }
                return true;
              })
            )
            .subscribe(() => {
              if (action?.payload) {
                this.customContentService.checkoutNode();
              } else {
                let interval$ = interval();
                if (this.router.url.split('?')[0].includes('edit')) {
                  this.citationService.disableSaveButton.next(true);
                  interval$ = interval(10);
                }
                interval$
                  .pipe(
                    take(1),
                    switchMap(() => {
                      const { key, ...rest } =
                        CommonConstants.CONFIRM_CANCEL_CHECKOUT;
                      if (this.appConfig.get(key)) {
                        return this.dialogRef
                          .open(WkConfirmCancelCheckoutComponent, {
                            data: { ...rest },
                            width: '500px',
                          })
                          .afterClosed()
                          .pipe(take(1));
                      }
                      return of(true);
                    }),
                    filter((confirm: boolean) => confirm),
                    switchMap(() =>
                      this.store.select(getAppSelection).pipe(take(1))
                    )
                  )
                  .subscribe((selection) => {
                    if (selection && !selection.isEmpty) {
                      const node = selection.nodes[0].entry;
                      const batchNodesIds = selection.nodes.map(
                        (nodeSelected) => nodeSelected.entry.id
                      );
                      const cancelCheckoutBatchParams = {
                        ids: batchNodesIds,
                      };

                      from(
                        this.apiService.callContentClientCustomApi({
                          path: `alfresco/service/wk/nodes/cancel-checkout`,
                          httpMethod: 'POST',
                          bodyParam: cancelCheckoutBatchParams,
                          contentTypes: jsonContentType,
                          accepts: jsonContentType,
                        })
                      ).subscribe({
                        next: (res: any) => {
                          this.updateCurrentUrl(res, node);
                        },
                        error: () => {
                          this.notificationService.openSnackMessage(
                            'Cannot Cancel Check-Out',
                            3000
                          );
                        },
                      });
                    }
                  });
              }
            });
        })
      ),
    { dispatch: false }
  );

  updateCurrentUrl(res, node, showCheckinSuccessMessage = false) {
    const nodeId = res.ids[0];
    if (showCheckinSuccessMessage) {
      this.store.dispatch(
        new SnackbarInfoAction('APP.ACTIONS.CHECKIN_SUCCESS_NOTIFICATION')
      );
    }
    this.customContentService.navigatePreviewMode(node, nodeId);
    this.customContentService.checkoutNode();
  }
}
